import * as React from 'react';
import { History, PenSquare, RefreshCw, Trash2, X } from 'lucide-react';
import './conversationLook.css';
import '../SkillRunner.css';
import '../BankOfChangesRunner.css';

/** One saved conversation, as a row of the list says it. */
export type SavedConversationRow = {
  id: string;
  title: string;
  /** A run of this conversation is going on in the server right now. */
  running: boolean;
  /** Where it stands and when it was last touched; while it runs, what is being done. */
  detail: string;
};

export type SavedConversationsLabels = {
  title: string;
  close: string;
  newConversation: string;
  loading: string;
  empty: string;
  current: string;
  confirmDelete: string;
  confirmDeleteYes: string;
  confirmDeleteNo: string;
  open: (title: string) => string;
  remove: (title: string) => string;
  openFailed: string;
  /** A line under the list; nothing when absent. */
  note?: string;
};

/** The account's conversations, as the server keeps them: open one, delete one, or start a new one. */
export const SavedConversations: React.FC<{
  labels: SavedConversationsLabels;
  rows: SavedConversationRow[] | null;
  currentId: string | null;
  failedId: string | null;
  onOpen: (id: string) => void;
  onDelete: (id: string) => void;
  onNew: () => void;
  onClose: () => void;
}> = ({ labels, rows, currentId, failedId, onOpen, onDelete, onNew, onClose }) => {
  const [confirming, setConfirming] = React.useState<string | null>(null);
  return (
    <div className="boc-sessions" role="dialog" aria-label={labels.title}>
      <div className="boc-sessions-head">
        <span className="boc-sessions-title">{labels.title}</span>
        <button type="button" className="boc-icon-btn" title={labels.close} aria-label={labels.close} onClick={onClose}>
          <X size={16} aria-hidden />
        </button>
      </div>
      <button type="button" className="boc-sessions-new" onClick={onNew}>
        <PenSquare size={15} aria-hidden />
        {labels.newConversation}
      </button>
      <div className="boc-sessions-list">
        {rows === null && <div className="boc-sessions-empty">{labels.loading}</div>}
        {rows !== null && rows.length === 0 && <div className="boc-sessions-empty">{labels.empty}</div>}
        {(rows ?? []).map((item) => (
          <div
            key={item.id}
            className={`boc-session${item.id === currentId ? ' boc-session--current' : ''}${item.running ? ' boc-session--running' : ''}`}
            data-running={item.running ? 'true' : undefined}
          >
            {confirming === item.id ? (
              <div className="boc-session-confirm">
                <span>{labels.confirmDelete}</span>
                <button
                  type="button"
                  className="boc-session-confirm-yes"
                  onClick={() => {
                    setConfirming(null);
                    onDelete(item.id);
                  }}
                >
                  {labels.confirmDeleteYes}
                </button>
                <button type="button" className="boc-session-confirm-no" onClick={() => setConfirming(null)}>
                  {labels.confirmDeleteNo}
                </button>
              </div>
            ) : (
              <>
                <button type="button" className="boc-session-open" aria-label={labels.open(item.title)} onClick={() => onOpen(item.id)}>
                  <span className="boc-session-title" dir="auto">
                    {item.title}
                  </span>
                  {item.running ? (
                    // A run of this conversation is going on in the server right now.
                    <span className="boc-session-detail boc-session-running" role="status">
                      <RefreshCw size={11} className="spinning" aria-hidden />
                      {item.id === currentId ? `${labels.current} · ` : ''}
                      {item.detail}
                    </span>
                  ) : (
                    <span className="boc-session-detail">
                      {item.id === currentId ? `${labels.current} · ` : ''}
                      {item.detail}
                    </span>
                  )}
                  {failedId === item.id && <span className="boc-session-failed">{labels.openFailed}</span>}
                </button>
                <button
                  type="button"
                  className="boc-icon-btn boc-session-delete"
                  title={labels.remove(item.title)}
                  aria-label={labels.remove(item.title)}
                  onClick={() => setConfirming(item.id)}
                >
                  <Trash2 size={15} aria-hidden />
                </button>
              </>
            )}
          </div>
        ))}
      </div>
      {labels.note && <div className="boc-sessions-note">{labels.note}</div>}
    </div>
  );
};

/** The two buttons at the head of a conversation: the saved conversations, and a new one. */
export const ConversationHeaderActions: React.FC<{
  savedLabel: string;
  /** What the history button says when hovered: how many are saved, or how many are being worked on. */
  savedTitle: string;
  newLabel: string;
  savedCount: number;
  runningCount: number;
  panelOpen: boolean;
  onTogglePanel: () => void;
  onNew: () => void;
}> = ({ savedLabel, savedTitle, newLabel, savedCount, runningCount, panelOpen, onTogglePanel, onNew }) => (
  <div className="boc-header-actions">
    <button type="button" className="boc-icon-btn boc-history-btn" title={savedTitle} aria-expanded={panelOpen} onClick={onTogglePanel}>
      <History size={16} aria-hidden />
      <span className="ag-visually-hidden">{savedLabel}</span>
      {savedCount > 0 && (
        <span className="boc-history-count" aria-hidden>
          {savedCount}
        </span>
      )}
      {/* A conversation is being worked on in the server: the button says so even with the list closed. */}
      {runningCount > 0 && <span className="boc-history-running" aria-hidden />}
    </button>
    <button type="button" className="boc-icon-btn boc-new-btn" title={newLabel} onClick={onNew}>
      <PenSquare size={16} aria-hidden />
      <span className="ag-visually-hidden">{newLabel}</span>
    </button>
  </div>
);
